import { useState, useEffect, useCallback } from 'react';

import { storeMenusListener } from '~/firebase/listeners/store';

import { atom, useSetRecoilState } from 'recoil';

import useStoreItems from '~/hooks/useStoreItems';
import useStoreCategories from '~/hooks/useStoreCategories';

import * as fp from 'lodash/fp';

import isInOperatingTime from '~/utils/isInOperatingTime';
import getDateInventory from '~/utils/getDateInventory';

import { CollectionSnapShotFn } from '~/firebase/listeners/type';
import { MenuWithId, Menu } from '~/types/store/menu';
import { CategoryWithId } from '~/types/store/category';
import { ItemWithId } from '~/types/store/item';

export type CategoryWithItems = Omit<CategoryWithId, 'items'> & {
  items: Array<ItemWithId>;
};

export const storeAvailableItemList = atom<Array<CategoryWithItems>>({
  key: 'storeAvailableItemList',
  default: [],
});

export default function useStoreMenuList(storeId: string, pickMealTime: Date | null) {
  const setStoreAvailableItemList = useSetRecoilState(storeAvailableItemList);

  const itemsMap = useStoreItems(storeId);
  const categoriesMap = useStoreCategories(storeId);

  const [availableMenus, setAvailableMenus] = useState<Array<MenuWithId>>([]);
  const [availableCategoriesId, setAvailableCategoriesId] = useState<Array<string>>([]);

  const handleMenusSnapshot = useCallback<CollectionSnapShotFn>(
    (snap) => {
      const menus = snap.docs.map((doc) => {
        const data = doc.data() as Menu;
        return {
          id: doc.id,
          ...data,
        };
      });

      const availableMenus = fp.pipe<[Array<MenuWithId>], Array<MenuWithId>, Array<MenuWithId>>(
        fp.filter(({ allowPublish }) => allowPublish),
        fp.filter(({ schedule }) => isInOperatingTime(schedule, pickMealTime)),
      )(menus);

      const uniqCategoriesId = fp.pipe<[Array<MenuWithId>], Array<string>, Array<string>>(
        fp.flatMap(({ categories }) => categories),
        fp.uniq,
      )(availableMenus);

      setAvailableMenus(availableMenus);
      setAvailableCategoriesId(uniqCategoriesId.sort());
    },
    [setAvailableMenus, setAvailableCategoriesId, pickMealTime],
  );
  useEffect(() => {
    if (storeId) {
      const unlisten = storeMenusListener(storeId, handleMenusSnapshot);
      return () => {
        unlisten();
      };
    }
  }, [storeId, pickMealTime]);

  useEffect(() => {
    setStoreAvailableItemList(
      availableCategoriesId.reduce<Array<CategoryWithItems>>((acc, categoryId) => {
        const category = categoriesMap.get(categoryId);
        if (!category) {
          return acc;
        }

        const items = category.items.reduce<Array<ItemWithId>>((acc, itemId) => {
          const item = itemsMap.get(itemId);
          if (
            item &&
            item.isAvailable &&
            fp.gt(getDateInventory(item.inventory, pickMealTime), 0)
          ) {
            acc.push(item);
          }
          return acc;
        }, []);

        if (fp.gt(items.length, 0)) {
          acc.push({ ...category, items });
        }
        return acc;
      }, []),
    );
  }, [availableCategoriesId, itemsMap, categoriesMap, pickMealTime, setStoreAvailableItemList]);
}
